import { selectors as authSelectors } from '@hixme/auth-ui'

import { getEmployeeExperienceBaseUrl } from 'modules/api/employee-experience'
import { NAME as USER_SESSION_NAME } from 'store/modules/user-session/constants'

const getUserId = (state: unknown): string | undefined =>
  (state as Record<string, { userId?: string } | undefined>)[USER_SESSION_NAME]?.userId

export const SHP_LAST_BRIDGED_EMPLOYEE_KEY = 'shp:lastBridgedEmployee'

export const clearShopHealthSession = (): void => {
  const base = getEmployeeExperienceBaseUrl()
  fetch(`${base}/api/v1/employee-experience/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
  }).catch(() => {})
}

export const clearShpBridgedIdentity = (): void => {
  localStorage.removeItem(SHP_LAST_BRIDGED_EMPLOYEE_KEY)
}

export const clearShpSessionOnIdentityChange = (): void => {
  clearShopHealthSession()
  clearShpBridgedIdentity()
}

export const setShpLastBridgedEmployee = (employeePublicKey: string): void => {
  if (employeePublicKey) {
    localStorage.setItem(SHP_LAST_BRIDGED_EMPLOYEE_KEY, employeePublicKey)
  }
}

/** Bootstrap fail-safe: clear when marker is missing or names a different employee. */
export const ensureShpIdentityMatches = (currentEmployeeKey: string): void => {
  const marker = localStorage.getItem(SHP_LAST_BRIDGED_EMPLOYEE_KEY)
  if (!marker || marker !== currentEmployeeKey) {
    clearShopHealthSession()
    clearShpBridgedIdentity()
  }
}

/** Clear only when a bridged marker exists for another employee (safe for repeat initUserId). */
export const clearShpSessionIfBridgedEmployeeMismatch = (currentEmployeeKey: string): void => {
  const marker = localStorage.getItem(SHP_LAST_BRIDGED_EMPLOYEE_KEY)
  if (marker && marker !== currentEmployeeKey) {
    clearShpSessionOnIdentityChange()
  }
}

type StoreLike = {
  getState: () => unknown
  subscribe: (listener: () => void) => () => void
}

type ShpIdentitySyncDeps = {
  // Injected to avoid an epsSession <-> shopHealthSession import cycle.
  whenEpsSessionReady: () => Promise<void>
}

export const setupShpIdentitySync = (
  store: StoreLike,
  { whenEpsSessionReady }: ShpIdentitySyncDeps
): void => {
  let done = false
  let unsubscribe: (() => void) | null = null

  const finish = () => {
    if (done) return
    done = true
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
  }

  const attempt = () => {
    if (done) return

    const state = store.getState() || {}
    const credentials = authSelectors.getCredentials(state)

    if (!credentials?.idToken) return

    const currentEmployeeKey = getUserId(state)
    if (!currentEmployeeKey) return

    // Stop watching now (one-shot); defer the fail-safe past the in-flight bridge-login (which
    // overwrites the cookie + marker), then clear only if the marker still mismatches — no race.
    finish()

    const runFailSafeAfterBridgeLogins = () => {
      const pending = whenEpsSessionReady()
      pending
        .then(() => {
          // A newer bridge-login was queued while waiting; follow it before deciding.
          if (whenEpsSessionReady() !== pending) {
            runFailSafeAfterBridgeLogins()
            return
          }
          const current = getUserId(store.getState() || {})
          if (current) ensureShpIdentityMatches(current)
        })
        .catch(() => {})
    }
    runFailSafeAfterBridgeLogins()
  }

  unsubscribe = store.subscribe(attempt)
  attempt()
}
